import { useEffect } from "react";
import { api } from "@/api/client";
import { useStore } from "@/state/store";
import type { WinningTopicState } from "@/state/store";

/**
 * Resolves the winning topic (most votes; ties broken by `topic.order`).
 *
 * Reads from the central store — actual polling happens in
 * `useWinningTopicPolling()`, which is mounted once at app-Shell level.
 * That collapses N components × M instances into a single fetch loop,
 * which matters when 64 phones poll simultaneously.
 *
 * Returns null until both topics + tallies are loaded, OR when all
 * votes have been reset (so the UI can clear stale winners).
 */
export function useWinningTopic(): WinningTopicState | null {
  return useStore().state.workshop.winningTopic;
}

/**
 * Single, app-level polling loop. Mount exactly ONCE (Shell). 10s cadence
 * — the winner barely flips during the working phase, but a reset must
 * be detected within a couple of cycles.
 */
export function useWinningTopicPolling(intervalMs = 10000) {
  const { state, dispatch } = useStore();
  const token = state.auth.token;

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    const load = async () => {
      try {
        const [t, v] = await Promise.all([
          api.topics.list(token),
          api.votes.tallies(token),
        ]);
        if (cancelled) return;

        // Reset-bug fix: empty tallies → clear, don't keep a stale winner.
        if (v.tallies.length === 0) {
          dispatch({ type: "winningTopic/set", value: null });
          return;
        }

        const counts = new Map(v.tallies.map((x) => [x.topicId, x.count]));
        const ranked = [...t.topics]
          .map((topic) => ({ topic, count: counts.get(topic.id) ?? 0 }))
          .sort(
            (a, b) => b.count - a.count || a.topic.order - b.topic.order,
          );
        const top = ranked[0];
        if (top && top.count > 0) {
          dispatch({
            type: "winningTopic/set",
            value: { topic: top.topic, count: top.count },
          });
        } else {
          dispatch({ type: "winningTopic/set", value: null });
        }
      } catch {
        /* swallow transient errors */
      }
    };
    load();
    const id = setInterval(load, intervalMs);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [token, intervalMs, dispatch]);
}
